import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Chip, Stack
} from '@mui/material';
import MediaLibrary from '../../common/MediaLibrary';

interface CategoryData {
    title: string;
    order: number;
    description: string;
    imageUrl: string;
    slug: string;
    competency: string;
    tags: string[];
}

interface CategoryModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CategoryData) => void;
    initialData?: Partial<CategoryData>;
    availableCompetencies?: string[];
    isEditing?: boolean;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ open, onClose, onSave, initialData, availableCompetencies = [], isEditing = false }) => {
    const [data, setData] = useState<CategoryData>({
        title: '',
        order: 0,
        description: '',
        imageUrl: '',
        slug: '',
        competency: '',
        tags: []
    });
    const [tagInput, setTagInput] = useState('');
    const [openMediaLibrary, setOpenMediaLibrary] = useState(false);

    const [errors, setErrors] = useState<{ title?: string; slug?: string }>({});

    useEffect(() => {
        if (open) {
            if (initialData) {
                setData({
                    title: initialData.title || '',
                    order: initialData.order || 0,
                    description: initialData.description || '',
                    imageUrl: initialData.imageUrl || '',
                    slug: initialData.slug || '',
                    competency: initialData.competency || '',
                    tags: initialData.tags || []
                });
            } else {
                setData({
                    title: '',
                    order: 0,
                    description: '',
                    imageUrl: '',
                    slug: '',
                    competency: '',
                    tags: []
                });
            }
            setTagInput('');
            setErrors({});
        }
    }, [open, initialData]);

    const handleAddTag = () => {
        if (tagInput.trim()) {
            setData({ ...data, tags: [...data.tags, tagInput.trim()] });
            setTagInput('');
        }
    };

    const handleDeleteTag = (tagToDelete: string) => {
        setData({ ...data, tags: data.tags.filter(t => t !== tagToDelete) });
    };

    const generateSlug = (text: string) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')     // Replace spaces with -
            .replace(/[^\w\-]+/g, '') // Remove all non-word chars
            .replace(/\-\-+/g, '-');  // Replace multiple - with single -
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        // Only auto-generate slug if we are creating a new category or if the slug was empty/auto-generated
        // For simplicity, let's auto-update slug if it matches the old title's slug or is empty
        const currentSlug = data.slug;
        const autoSlug = generateSlug(title);

        setData(prev => ({
            ...prev,
            title,
            slug: (!isEditing && (currentSlug === '' || currentSlug === generateSlug(prev.title))) ? autoSlug : currentSlug
        }));

        if (errors.title) {
            setErrors(prev => ({ ...prev, title: undefined }));
        }
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setData({ ...data, slug: e.target.value });
        if (errors.slug) {
            setErrors(prev => ({ ...prev, slug: undefined }));
        }
    };

    const handleSave = () => {
        const newErrors: { title?: string; slug?: string } = {};
        if (!data.title.trim()) {
            newErrors.title = 'Le titre est requis';
        }
        if (!data.slug.trim()) {
            newErrors.slug = 'Le slug est requis';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave(data);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? 'Modifier la catégorie' : 'Ajouter une catégorie'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Titre de la catégorie"
                        fullWidth
                        required
                        value={data.title}
                        onChange={handleTitleChange}
                        error={!!errors.title}
                        helperText={errors.title}
                    />
                    <TextField
                        margin="dense"
                        label="Ordre"
                        type="number"
                        fullWidth
                        value={data.order}
                        onChange={(e) => setData({ ...data, order: parseInt(e.target.value) || 0 })}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={data.description}
                        onChange={(e) => setData({ ...data, description: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Slug"
                        fullWidth
                        required
                        value={data.slug}
                        onChange={handleSlugChange}
                        error={!!errors.slug}
                        helperText={errors.slug || "Identifiant unique pour l'URL (généré automatiquement)"}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            margin="dense"
                            label="Image URL"
                            fullWidth
                            value={data.imageUrl}
                            onChange={(e) => setData({ ...data, imageUrl: e.target.value })}
                        />
                        <Button sx={{ mt: 1 }} variant="outlined" onClick={() => setOpenMediaLibrary(true)}>
                            Select Image
                        </Button>
                    </Box>

                    <TextField
                        select
                        margin="dense"
                        label="Competency Affiliation"
                        fullWidth
                        value={data.competency}
                        onChange={(e) => setData({ ...data, competency: e.target.value })}
                        SelectProps={{ native: true }}
                    >
                        <option value=""></option>
                        {availableCompetencies.map((comp, index) => (
                            <option key={index} value={comp}>{comp}</option>
                        ))}
                    </TextField>

                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label="Add Tag"
                                fullWidth
                                size="small"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                            />
                            <Button variant="outlined" onClick={handleAddTag}>Add</Button>
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                            {data.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    onDelete={() => handleDeleteTag(tag)}
                                />
                            ))}
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">{isEditing ? 'Save' : 'Create'}</Button>
                </DialogActions>
            </Dialog>

            <MediaLibrary
                open={openMediaLibrary}
                onClose={() => setOpenMediaLibrary(false)}
                onSelect={(url) => setData({ ...data, imageUrl: url })}
            />
        </>
    );
};

export default CategoryModal;
